import { formatMealPlanDate } from "@/helpers/useCommonFunctions";
import { RecipeImage, SORT_ORDER } from "@/utils/constant";
import { BreadcrumbData } from "@/utils/types/recipes";

// Parse Html
export const decodeHtml = (html: any) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.documentElement.textContent;
};

// Change algolia values to slug
export const toSlug = (str: string) => {
  const encodeSlug = str
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");

  return encodeURIComponent(encodeSlug);
};

// Print the Recipes detail
export const handlePrint = () => {
  window.print();
};

export const breadcrumbListRecipeDetails = (
  slug: string,
  title: string
): BreadcrumbData[] => [
  { text: "Home", href: "/" },
  { text: "Recipes", href: "/recipes" },
  { text: title ?? slug, href: "" },
];
//Returns a formatted metric string from quantity and unit.
export const buildMetricString = (item: any): string => {
  const metric = item?.quantity?.metric;
  const unit = item?.unit?.metric;

  if (typeof metric === "string" && metric.match(/[a-zA-Z]/)) {
    return metric;
  }
  if (metric && unit) {
    return `${metric} ${unit}`;
  }

  if (metric && !unit) {
    return `${metric} gm`; //Consider default to gram
  }

  return "";
};

//Parses a metric string and converts the value to grams.
export const convertToGm = (met: string): number => {
  if (!met) return 0;

  const match =
    typeof met === "string"
      ? met
          .trim()
          .toLowerCase()
          .match(/^([\d.]+)\s*(ml|l|gm|g|kg)?$/)
      : null;

  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2] || "gm";

  switch (unit) {
    case "l":
    case "kg":
      return value * 1000;
    case "ml":
    case "g":
    case "gm":
    default:
      return value;
  }
};
export const getSortedIngredient = (ingredient: any) => {
  const result: any = ingredient
    .map((item: any) => ({
      ...item,
      quantity: {
        ...item.quantity,
        MetInGm: convertToGm(item.quantity.Met),
      },
    }))
    .reduce((maxItem: any, item: any) => {
      return item.quantity.MetInGm > maxItem.quantity.MetInGm ? item : maxItem;
    });

  return result.ingredient;
};

export const createMealPayload = (
  mealTypeId: number,
  recipeId: string,
  token: string | null,
  scheduleDate?: string
) => ({
  type: "Add",
  meal_type_id: mealTypeId,
  recipe_ids: recipeId ? [recipeId] : [],
  token: token,
  schedule_date: scheduleDate || formatMealPlanDate(new Date()),
});

export const updateOGMeta = (recipeDetail: any, currentUrl: string) => {
  const imageUrl = `${RecipeImage}${recipeDetail?.featuredImage}`;
  const metaTags = [
    {
      property: "og:title",
      content: `${recipeDetail?.title} | Recipe Details- Sobeys Inc.`,
    },
    { property: "og:description", content: recipeDetail?.metaDescription },
    { property: "og:locale", content: "en_US" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: currentUrl },
    { property: "og:image", content: imageUrl },
    { property: "og:image:secure_url", content: imageUrl },
    { property: "og:image:width", content: "1000" },
    { property: "og:image:height", content: "650" },
    {
      property: "og:image:alt",
      content: recipeDetail?.title || "Recipe Image",
    },
    { property: "og:image:type", content: "image/jpeg" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: recipeDetail?.title },
    { name: "twitter:description", content: recipeDetail?.metaDescription },
    { name: "twitter:image", content: imageUrl },
  ];

  metaTags.forEach(({ property, name, content }) => {
    const metaTag = document.querySelector(
      `meta[${property ? "property" : "name"}="${property || name}"]`
    );

    if (metaTag) {
      metaTag.setAttribute("content", content);
    } else {
      const newMeta = document.createElement("meta");
      if (property) {
        newMeta.setAttribute("property", property);
      } else {
        newMeta.setAttribute("name", name as string);
      }
      newMeta.setAttribute("content", content);
      document.head.appendChild(newMeta);
    }
  });
};

const fractionMap: Record<string, string> = {
  "1/2": "½",
  "1/3": "⅓",
  "2/3": "⅔",
  "1/4": "¼",
  "3/4": "¾",
  "1/5": "⅕",
  "2/5": "⅖",
  "3/5": "⅗",
  "4/5": "⅘",
  "1/6": "⅙",
  "5/6": "⅚",
  "1/8": "⅛",
  "3/8": "⅜",
  "5/8": "⅝",
  "7/8": "⅞",
};

export const formatQuantityValue = (value: string) => {
  return value.replace(/(\d)\/(\d)/, (_, numerator, denominator) => {
    const fraction = `${numerator}/${denominator}`;
    return fractionMap[fraction] || fraction;
  });
};

export const aemSortOrder = (aemFilterData: any) => {
  const sortOrder = [];

  sortOrder.push({
    attribute: SORT_ORDER[0].attribute,
    name: aemFilterData.tags || "Tags",
  });

  if (aemFilterData?.skillLevel) {
    sortOrder.push({
      attribute: SORT_ORDER[1].attribute,
      name: aemFilterData.skillLevel,
    });
  }

  if (aemFilterData?.totalTime) {
    sortOrder.push({
      attribute: SORT_ORDER[2].attribute,
      name: aemFilterData.totalTime,
    });
  }

  return sortOrder;
};

export const getFilteredRecipes = (originalRecipeList: any, value: string) => {
  const filtered = originalRecipeList
    .map((list: any) => {
      const matchingRecipes = list.recipes.filter((recipe: any) =>
        recipe.title?.toLowerCase().includes(value)
      );

      return matchingRecipes.length
        ? { ...list, recipes: matchingRecipes }
        : null;
    })
    .filter(Boolean);
  return filtered;
};

export const updateListName = (lists: any, data: any) => {
  return lists.map((list: { id: any }) =>
    list.id === data?.id
      ? {
          ...list,
          list_name: data?.list_name,
        }
      : list
  );
};
